import { useEffect, useCallback, useRef } from "react";
import CloseIcon from "@/common/images/icons/cross.svg?react";
import ArrowIcon from "@/common/images/icons/chevron.svg?react";
import { BASE_BUTTON } from "./constants";
import "./style/Popup.scss";
import { clsx } from "clsx";
import { useBodyLock } from "@/common/hooks/useBodyLock";
import { Button } from "@/components/buttons/Button";

interface IBtnProps extends React.ComponentPropsWithRef<"button"> {
  variant?: number;
  formId?: string;
  title?: string;
}

interface IPopupProps {
  onClose?: () => void;
  onBack?: () => void;
  isOpen: boolean;
  title?: string | React.ReactNode;
  children: React.ReactNode;
  popupClass?: string;
  btnProps?: IBtnProps;
  disableBackdropClose?: boolean;
}

export const Popup = (props: IPopupProps) => {
  const {
    onClose,
    onBack,
    isOpen,
    title,
    children,
    popupClass,
    disableBackdropClose,
    btnProps: {
      variant = undefined,
      title: btnTitle = undefined,
      onClick = undefined,
      disabled = undefined,
      formId = undefined,
      type = undefined
    } = {}
  } = props;
  const popupRef = useRef<HTMLInputElement>(null);

  useBodyLock(isOpen);

  const keyPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && onClose) {
        onClose();
      }
    },
    [onClose, isOpen]
  );

  useEffect(() => {
    document.addEventListener("keydown", keyPress);

    return () => document.removeEventListener("keydown", keyPress);
  }, [keyPress]);

  const closePopup = (e: React.MouseEvent) => {
    if (popupRef.current === e.target && onClose) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      onClick={!disableBackdropClose ? closePopup : undefined}
      ref={popupRef}
      className={clsx("PopupBackdrop", popupClass)}
    >
      <div className="Popup">
        {(title || onClose || onBack) && (<div className="Popup-Top">
          {title && (
            <div className="Popup-Header">
              {typeof title === "string" ? (
                <h4>{title}</h4>
              ) : title}
            </div>
          )}
          {onClose && <button className="Popup-Close" onClick={onClose} aria-label="close popup">
            <CloseIcon />
          </button>}
          {onBack && <button className="Popup-Back" onClick={onBack} aria-label="back to the previous step"><ArrowIcon /></button>}
        </div>)}
        <div className="Popup-Content">
          {children}
        </div>
        {variant === BASE_BUTTON && (
          <div className="Popup-BtnBase">
            <Button variant="primary" fullWidth onClick={onClick} disabled={disabled} form={formId} type={type} aria-label={btnTitle}>
              {btnTitle}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
